import React, { useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/axiosInstance';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartData, ChartOptions } from 'chart.js';
import { Box } from '@mui/material';
import { PeriodType } from '../periodToggle';
import { isWithinInterval } from 'date-fns';

ChartJS.register(ArcElement, Tooltip, Legend);

interface CategoryStat {
  name: string;
  total: number;
  date?: string;
}

interface CategoryPieProps {
  dataType: 'income' | 'expense';
  period: PeriodType;
  from?: string;
  to?: string;
}

const hashToColor = (s: string, alpha = 0.7) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const r = 40 + (h % 160);
  const g = 40 + ((h >> 8) % 160);
  const b = 40 + ((h >> 16) % 160);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CategoryPie: React.FC<CategoryPieProps> = ({ dataType, period, from, to }) => {
  const endpoint = dataType === 'income' ? 'categories-income/stats' : 'categories-expense/stats';

  const queryParams = useMemo(() => {
    const params: Record<string, string> = { period };

    if (period === 'custom') {
      if (from) params.from = from;
      if (to) params.to = to;
    }

    return params;
  }, [period, from, to]);

  const { data = [] } = useQuery<CategoryStat[]>({
    queryKey: ['category-pie', dataType, period, from, to],
    queryFn: () => api.get(`/${endpoint}`, { params: queryParams }).then(r => r.data),
  });

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const prevYear = currentYear - 1;

  const filtered = useMemo(() => {
    return data.filter(d => {
      if (!d.date) return false;

      const dt = new Date(d.date);
      if (Number.isNaN(dt.getTime())) return false;
      if (dt.getTime() > now.getTime()) return false;

      if (period === 'custom') {
        if (!from || !to) return false;

        const start = new Date(from);
        const end = new Date(to);

        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
          return false;
        }

        end.setHours(23, 59, 59, 999);
        return isWithinInterval(dt, { start, end });
      }

      if (period === 'years') {
        const y = dt.getFullYear();
        return y === currentYear || y === prevYear;
      }

      if (period === 'year') {
        return dt.getFullYear() === currentYear;
      }

      if (period === 'month') {
        return dt.getFullYear() === currentYear && dt.getMonth() === now.getMonth();
      }

      const weekStart = new Date(now);
      const day = weekStart.getDay();
      const diff = day === 0 ? 6 : day - 1;
      weekStart.setDate(weekStart.getDate() - diff);
      weekStart.setHours(0, 0, 0, 0);

      return dt.getTime() >= weekStart.getTime() && dt.getTime() <= now.getTime();
    });
  }, [data, period, now, currentYear, prevYear, from, to]);

  const rows = useMemo(() => {
    const map = new Map<string, number>();

    filtered.forEach(d => {
      map.set(d.name, (map.get(d.name) ?? 0) + Number(d.total || 0));
    });

    return Array.from(map.entries())
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  const totalSum = useMemo(() => rows.reduce((a, r) => a + r.total, 0), [rows]);

  const labelsWithPct = useMemo(() => {
    return rows.map(r => {
      const p = totalSum > 0 ? ((r.total / totalSum) * 100).toFixed(1) : '0.0';
      return `${r.name} (${p}%)`;
    });
  }, [rows, totalSum]);

  const chartData: ChartData<'pie'> = useMemo(
    () => ({
      labels: labelsWithPct,
      datasets: [
        {
          data: rows.map(r => r.total),
          backgroundColor: rows.map(r => hashToColor(r.name, 0.7)),
        },
      ],
    }),
    [rows, labelsWithPct],
  );

  const options: ChartOptions<'pie'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            padding: 12,
            font: ctx => {
              const w = ctx?.chart?.width ?? 400;
              const size = w < 380 ? 11 : 13;
              return { size };
            },
          },
        },
        tooltip: {
          callbacks: {
            label: ctx => {
              const v = Number(ctx.parsed || 0);
              const idx = ctx.dataIndex;
              const name = rows[idx]?.name ?? '';
              const p = totalSum > 0 ? ((v / totalSum) * 100).toFixed(1) : '0.0';
              return `${name}: ${v} ₽ (${p}%)`;
            },
          },
        },
      },
    }),
    [rows, totalSum],
  );

  const hasData = rows.some(r => r.total > 0);

  return (
    <div>
      {hasData ? (
        <Box
          sx={{
            width: '100%',
            height: { xs: 320, sm: 420, md: 520 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Pie data={chartData} options={options} />
        </Box>
      ) : (
        <div>Нет записей для построения графика</div>
      )}
    </div>
  );
};

export default CategoryPie;
